import { useState, useRef, useEffect } from 'react';
import {
  getImpersonationUser,
  clearImpersonationSession,
  getAdminOrigin,
  isAllowedAdminOrigin,
} from '../utils/impersonationSession';

interface Props {
  onEnded: () => void;
}

// Builds the banner label preferring "First Last · Company" with graceful
// fallback to email or userId when name/company are missing.
function formatImpersonationLabel(u: {
  userId: string;
  userEmail: string;
  userFirstName?: string;
  userLastName?: string;
  companyName?: string;
  adminName?: string;
}): string {
  const name = `${u.userFirstName || ''} ${u.userLastName || ''}`.trim();
  const who = name || u.userEmail || u.userId;
  const userLabel = u.companyName ? `${who} · ${u.companyName}` : who;
  return u.adminName ? `${userLabel} (Admin: ${u.adminName})` : userLabel;
}

export function ImpersonationBanner({ onEnded }: Props) {
  const impUser = getImpersonationUser();
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endListenerRef = useRef<((e: MessageEvent) => void) | null>(null);
  const endTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (endListenerRef.current) {
        window.removeEventListener('message', endListenerRef.current);
      }
      if (endTimeoutRef.current) {
        clearTimeout(endTimeoutRef.current);
      }
    };
  }, []);

  if (!impUser) return null;

  const handleEnd = () => {
    setRequesting(true);
    setError(null);

    const adminOrigin = getAdminOrigin();
    if (!window.opener) {
      clearImpersonationSession();
      onEnded();
      window.close();
      return;
    }

    const targetOrigin = adminOrigin || '*';
    window.opener.postMessage({ type: 'END_IMPERSONATION_REQUEST' }, targetOrigin);

    const responseHandler = (e: MessageEvent) => {
      if (!isAllowedAdminOrigin(e.origin)) return;
      if (e.source !== window.opener) return;

      if (e.data?.type === 'END_IMPERSONATION_CONFIRMED') {
        if (endTimeoutRef.current) {
          clearTimeout(endTimeoutRef.current);
          endTimeoutRef.current = null;
        }
        window.removeEventListener('message', responseHandler);
        endListenerRef.current = null;
        clearImpersonationSession();
        onEnded();
        window.close();
        return;
      }

      if (e.data?.type === 'END_IMPERSONATION_FAILED') {
        if (endTimeoutRef.current) {
          clearTimeout(endTimeoutRef.current);
          endTimeoutRef.current = null;
        }
        window.removeEventListener('message', responseHandler);
        endListenerRef.current = null;
        setError((e.data.message as string) || 'Failed to end impersonation. Please try again.');
        setRequesting(false);
      }
    };

    endListenerRef.current = responseHandler;
    window.addEventListener('message', responseHandler);

    endTimeoutRef.current = setTimeout(() => {
      window.removeEventListener('message', responseHandler);
      endListenerRef.current = null;
      endTimeoutRef.current = null;
      setError('Admin tab did not respond. Close this tab to exit impersonation.');
      setRequesting(false);
    }, 10000);
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="w-full bg-amber-500 text-white flex flex-col"
      style={{ position: 'sticky', top: 0, zIndex: 9999 }}
    >
      <div className="flex items-center justify-between px-4 py-2">
        <span className="text-sm font-semibold">
          Admin impersonation session — viewing as{' '}
          <strong>{formatImpersonationLabel(impUser)}</strong>
        </span>
        <button
          onClick={handleEnd}
          disabled={requesting}
          className="ml-4 px-3 py-1 rounded bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white text-xs font-semibold transition-colors"
        >
          {requesting ? 'Ending…' : 'End Impersonation'}
        </button>
      </div>
      {error && <div className="px-4 pb-2 text-xs text-amber-100">{error}</div>}
    </div>
  );
}
